import { forwardRef, useImperativeHandle, useState, useCallback } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";

// ── Types ──────────────────────────────────────────────────────────────────────

type Surface = "vestibular" | "lingual" | "mesial" | "distal" | "oclusal";

type PerZoneCondition = "caries" | "obturacion" | "sellante" | "fractura";
type WholeToothCondition = "ausente" | "extraccion_indicada" | "corona" | "endodoncia";
type Condition = PerZoneCondition | WholeToothCondition;

type ToothState = {
  surfaces: Record<Surface, PerZoneCondition | null>;
  toothCondition: WholeToothCondition | null;
  note: string;
};

export type SimpleOdontogramData = {
  version: "simple-v1";
  showPrimary: boolean;
  teeth: Record<string, ToothState>;
};

export type SimpleOdontogramHandle = {
  exportData: () => SimpleOdontogramData;
  importData: (json: unknown) => void;
};

// ── Constants ──────────────────────────────────────────────────────────────────

const PER_ZONE_SET = new Set(["caries", "obturacion", "sellante", "fractura"]);

const CONDITION_LABELS: Record<Condition, string> = {
  caries: "Caries",
  obturacion: "Obturación",
  sellante: "Sellante",
  fractura: "Fractura",
  ausente: "Pieza ausente",
  extraccion_indicada: "Extracción indicada",
  corona: "Corona",
  endodoncia: "Endodoncia",
};

const CONDITION_COLORS: Record<Condition, string> = {
  caries: "#ef4444",
  obturacion: "#3b82f6",
  sellante: "#16a34a",
  fractura: "#f97316",
  ausente: "#94a3b8",
  extraccion_indicada: "#dc2626",
  corona: "#7c3aed",
  endodoncia: "#0c4a6e",
};

// Per-zone conditions are applied to individual surfaces.
// Whole-tooth conditions are applied to the tooth as a whole.
const PER_ZONE_CONDITIONS: PerZoneCondition[] = ["caries", "obturacion", "sellante", "fractura"];
const WHOLE_CONDITIONS: WholeToothCondition[] = ["ausente", "extraccion_indicada", "corona", "endodoncia"];

// FDI layout — permanent teeth
const Q1 = [18, 17, 16, 15, 14, 13, 12, 11]; // upper right (patient's right → display left)
const Q2 = [21, 22, 23, 24, 25, 26, 27, 28]; // upper left
const Q3 = [31, 32, 33, 34, 35, 36, 37, 38]; // lower left
const Q4 = [48, 47, 46, 45, 44, 43, 42, 41]; // lower right (display left)

// Primary teeth
const PRI_UR = [55, 54, 53, 52, 51]; // upper right primary
const PRI_UL = [61, 62, 63, 64, 65]; // upper left primary
const PRI_LL = [71, 72, 73, 74, 75]; // lower left primary
const PRI_LR = [85, 84, 83, 82, 81]; // lower right primary

// ── SVG cell geometry (viewBox 36×36, inner inset 10px) ───────────────────────
//
//  The 5 zones:
//  vestibular : top trapezoid    0,0  36,0  26,10  10,10
//  lingual    : bottom trapezoid 0,36 36,36 26,26  10,26
//  mesial     : semantically "toward midline" — mapped to left or right
//               polygon depending on quadrant (see getPolygon)
//  distal     : semantically "away from midline" — opposite of mesial
//  oclusal    : center square    10,10 → 26,26

const SZ = 36;
const IN = 10; // corner inset

// Visual polygons — "near" means toward the display-left side
const POLY_VESTIBULAR = `0,0 ${SZ},0 ${SZ - IN},${IN} ${IN},${IN}`;
const POLY_LINGUAL = `0,${SZ} ${SZ},${SZ} ${SZ - IN},${SZ - IN} ${IN},${SZ - IN}`;
const POLY_LEFT = `0,0 0,${SZ} ${IN},${SZ - IN} ${IN},${IN}`;
const POLY_RIGHT = `${SZ},0 ${SZ},${SZ} ${SZ - IN},${SZ - IN} ${SZ - IN},${IN}`;

// Q1 and Q4: mesial is toward display-right (toward midline on right side)
// Q2 and Q3: mesial is toward display-left (toward midline on left side)
function getSurfacePolygon(surface: Surface, quadrant: 1 | 2 | 3 | 4): string {
  const mesialIsLeft = quadrant === 2 || quadrant === 3;
  switch (surface) {
    case "vestibular": return POLY_VESTIBULAR;
    case "lingual": return POLY_LINGUAL;
    case "mesial": return mesialIsLeft ? POLY_LEFT : POLY_RIGHT;
    case "distal": return mesialIsLeft ? POLY_RIGHT : POLY_LEFT;
    case "oclusal": return ""; // handled as <rect>
  }
}

const SURFACES: Surface[] = ["vestibular", "lingual", "mesial", "distal", "oclusal"];

// ── Empty state helpers ────────────────────────────────────────────────────────

const EMPTY_TOOTH: ToothState = {
  surfaces: { vestibular: null, lingual: null, mesial: null, distal: null, oclusal: null },
  toothCondition: null,
  note: "",
};

function emptyData(): SimpleOdontogramData {
  return { version: "simple-v1", showPrimary: false, teeth: {} };
}

function isSimpleV1(json: unknown): json is SimpleOdontogramData {
  return (
    !!json &&
    typeof json === "object" &&
    (json as SimpleOdontogramData).version === "simple-v1"
  );
}

function toothHasAny(t: ToothState): boolean {
  return (
    t.toothCondition !== null ||
    Object.values(t.surfaces).some((v) => v !== null) ||
    t.note.trim().length > 0
  );
}

// ── ToothCell ─────────────────────────────────────────────────────────────────

function ToothCell({
  toothNo,
  state,
  activeCondition,
  onClickSurface,
  onClickWhole,
  size = 36,
  isPrimary = false,
}: {
  toothNo: number;
  state: ToothState;
  activeCondition: Condition | null;
  onClickSurface: (surface: Surface) => void;
  onClickWhole: () => void;
  size?: number;
  isPrimary?: boolean;
}) {
  const tc = state.toothCondition;
  const isWholeModeActive = activeCondition !== null && !PER_ZONE_SET.has(activeCondition);

  // Determine quadrant from FDI number
  const hundreds = Math.floor(toothNo / 10);
  const quadrant = (
    hundreds === 1 || hundreds === 5 ? 1 :
    hundreds === 2 || hundreds === 6 ? 2 :
    hundreds === 3 || hundreds === 7 ? 3 : 4
  ) as 1 | 2 | 3 | 4;

  const strokeColor = isPrimary ? "#c0c8d4" : "#94a3b8";
  const strokeW = isPrimary ? "0.6" : "0.8";

  return (
    <svg
      viewBox={`0 0 ${SZ} ${SZ}`}
      width={size}
      height={size}
      style={{ display: "block", cursor: "pointer", userSelect: "none" }}
      aria-label={`Pieza ${toothNo}`}
    >
      {/* Background */}
      <rect x="0" y="0" width={SZ} height={SZ} fill="white" stroke={strokeColor} strokeWidth={strokeW} />

      {/* Whole-tooth condition overlays */}
      {tc === "ausente" && (
        <g>
          <rect x="0" y="0" width={SZ} height={SZ} fill="#f1f5f9" />
          <line x1="3" y1="3" x2={SZ - 3} y2={SZ - 3} stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
          <line x1={SZ - 3} y1="3" x2="3" y2={SZ - 3} stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
        </g>
      )}
      {tc === "extraccion_indicada" && (
        <g>
          <rect x="0" y="0" width={SZ} height={SZ} fill="#fff5f5" />
          <line x1="3" y1="3" x2={SZ - 3} y2={SZ - 3} stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" />
          <line x1={SZ - 3} y1="3" x2="3" y2={SZ - 3} stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" />
        </g>
      )}
      {tc === "corona" && (
        <rect x="2" y="2" width={SZ - 4} height={SZ - 4} fill="none" stroke="#7c3aed" strokeWidth="3" />
      )}
      {tc === "endodoncia" && (
        <circle cx={SZ / 2} cy={SZ / 2} r="6" fill="#0c4a6e" />
      )}

      {/* Surface zones — shown when no whole-tooth condition.
          strokeWidth 1.2 makes zone divisions clearly visible at small cell sizes. */}
      {!tc &&
        SURFACES.map((surface) => {
          const cond = state.surfaces[surface];
          // "transparent" (not "none") paints the fill with alpha=0, which is required
          // for SVG pointer events to fire on the zone's interior area.
          // fill="none" skips painting entirely → clicks only land on the 1.2px stroke border.
          const color = cond ? CONDITION_COLORS[cond] : "transparent";
          // In whole-tooth mode: clicking any zone applies the whole-tooth condition
          const onClick = isWholeModeActive
            ? () => onClickWhole()
            : () => onClickSurface(surface);

          if (surface === "oclusal") {
            return (
              <rect
                key="oclusal"
                x={IN}
                y={IN}
                width={SZ - IN * 2}
                height={SZ - IN * 2}
                fill={color}
                fillOpacity={cond ? 0.88 : 1}
                stroke="#64748b"
                strokeWidth="1.2"
                style={{ cursor: "pointer" }}
                onClick={onClick}
              />
            );
          }

          const poly = getSurfacePolygon(surface, quadrant);
          return (
            <polygon
              key={surface}
              points={poly}
              fill={color}
              fillOpacity={cond ? 0.88 : 1}
              stroke="#64748b"
              strokeWidth="1.2"
              style={{ cursor: "pointer" }}
              onClick={onClick}
            />
          );
        })}

      {/* Whole-tooth overlay click target — only active when tc is set but no surface zones
          are rendered (covers the X marks / ring / dot). pointerEvents:none when not needed
          avoids blocking zone clicks in per-zone mode. */}
      {tc && (
        <rect
          x="0"
          y="0"
          width={SZ}
          height={SZ}
          fill="transparent"
          style={{ cursor: "pointer" }}
          onClick={onClickWhole}
        />
      )}
    </svg>
  );
}

// ── TeethRow ──────────────────────────────────────────────────────────────────

function TeethRow({
  teeth,
  data,
  activeCondition,
  onClickSurface,
  onClickWhole,
  numberPosition,
  isPrimary = false,
}: {
  teeth: number[];
  data: SimpleOdontogramData;
  activeCondition: Condition | null;
  onClickSurface: (toothNo: number, surface: Surface) => void;
  onClickWhole: (toothNo: number) => void;
  numberPosition: "above" | "below";
  isPrimary?: boolean;
}) {
  const size = isPrimary ? 28 : 36;
  const fontSize = isPrimary ? "9px" : "10px";
  const gap = isPrimary ? 2 : 2;

  const cells = teeth.map((n) => {
    const state = data.teeth[String(n)] ?? EMPTY_TOOTH;
    return (
      <div
        key={n}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: `${gap}px`,
        }}
      >
        {numberPosition === "above" && (
          <span style={{ fontSize, color: "#64748b", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
            {n}
          </span>
        )}
        <ToothCell
          toothNo={n}
          state={state}
          activeCondition={activeCondition}
          onClickSurface={(surface) => onClickSurface(n, surface)}
          onClickWhole={() => onClickWhole(n)}
          size={size}
          isPrimary={isPrimary}
        />
        {numberPosition === "below" && (
          <span style={{ fontSize, color: "#64748b", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
            {n}
          </span>
        )}
      </div>
    );
  });

  // Split at midline for visual gap
  const mid = Math.floor(teeth.length / 2);
  const left = cells.slice(0, mid);
  const right = cells.slice(mid);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
      <div style={{ display: "flex", gap: "2px" }}>{left}</div>
      {/* Midline separator */}
      <div style={{ width: "6px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: "1px", height: isPrimary ? "28px" : "36px", background: "#cbd5e1" }} />
      </div>
      <div style={{ display: "flex", gap: "2px" }}>{right}</div>
    </div>
  );
}

// ── ConditionPalette ──────────────────────────────────────────────────────────

function ConditionChip({
  isActive,
  color,
  label,
  onClick,
}: {
  isActive: boolean;
  color: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={css({
        display: "flex",
        alignItems: "center",
        gap: "1.5",
        fontSize: "xs",
        fontWeight: "medium",
        px: "2.5",
        minH: "8",
        rounded: "md",
        borderWidth: "1px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "background 120ms ease, border-color 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
        _active: { transform: "scale(0.96)" },
      })}
      style={{
        background: isActive ? color + "18" : "white",
        borderColor: isActive ? color : "#e2e8f0",
        color: isActive ? color : "#374151",
      }}
    >
      <span
        style={{
          width: "10px",
          height: "10px",
          borderRadius: "2px",
          background: color,
          flexShrink: 0,
          display: "inline-block",
        }}
      />
      {label}
    </button>
  );
}

function ConditionPalette({
  active,
  onChange,
}: {
  active: Condition | null;
  onChange: (c: Condition | null) => void;
}) {
  const groups = [
    { label: "Por zona", items: PER_ZONE_CONDITIONS },
    { label: "Pieza completa", items: WHOLE_CONDITIONS },
  ];

  return (
    <div
      className={css({
        display: "flex",
        flexDir: "column",
        gap: "2.5",
        px: "4",
        py: "3",
        borderBottomWidth: "1px",
        borderColor: "#e2e8f0",
        bg: "#f8fafc",
      })}
    >
      {groups.map((group) => (
        <div key={group.label} className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
          <span className={css({ fontSize: "10px", fontWeight: "semibold", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "wide" })}>
            {group.label}
          </span>
          <div
            className={css({
              display: "grid",
              gridTemplateColumns: { base: "1fr 1fr", sm: "repeat(auto-fill, minmax(120px, max-content))" },
              gap: "1.5",
            })}
          >
            {group.items.map((cond) => (
              <ConditionChip
                key={cond}
                isActive={active === cond}
                color={CONDITION_COLORS[cond]}
                label={CONDITION_LABELS[cond]}
                onClick={() => onChange(active === cond ? null : cond)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Eraser — deselects active condition */}
      <button
        type="button"
        onClick={() => onChange(null)}
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "1.5",
          alignSelf: "flex-start",
          fontSize: "xs",
          fontWeight: "medium",
          px: "2.5",
          minH: "8",
          rounded: "md",
          borderWidth: "1px",
          cursor: "pointer",
          color: "#64748b",
          bg: active === null ? "#f1f5f9" : "white",
          borderColor: active === null ? "#94a3b8" : "#e2e8f0",
          transition: "background 120ms ease",
        })}
        title="Modo inspección / sin condición activa"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
          <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        Ninguna
      </button>
    </div>
  );
}

// ── SimpleOdontogramGrid ──────────────────────────────────────────────────────
//
// La paleta de condiciones (arriba de la grilla) ya combina color + etiqueta por
// cada chip, así que funciona como leyenda interactiva. No se repite una leyenda
// estática abajo para evitar la duplicación de la misma información dos veces.

export const SimpleOdontogramGrid = forwardRef<SimpleOdontogramHandle>(
  function SimpleOdontogramGrid(_, ref) {
    const [data, setData] = useState<SimpleOdontogramData>(emptyData());
    const [activeCondition, setActiveCondition] = useState<Condition | null>("caries");

    useImperativeHandle(ref, () => ({
      exportData: () => data,
      importData: (json: unknown) => {
        if (isSimpleV1(json)) {
          setData(json);
        } else {
          // Incompatible format (ZoliQua v1.3 etc.) — reset to blank
          setData(emptyData());
        }
      },
    }));

    const handleClickSurface = useCallback(
      (toothNo: number, surface: Surface) => {
        if (activeCondition === null) return;
        if (!PER_ZONE_SET.has(activeCondition)) {
          // Whole-tooth condition clicked on a zone → apply to whole tooth
          setData((prev) => applyWholeCondition(prev, toothNo, activeCondition as WholeToothCondition));
          return;
        }
        const cond = activeCondition as PerZoneCondition;
        setData((prev) => {
          const key = String(toothNo);
          const current = prev.teeth[key] ?? EMPTY_TOOTH;
          // If tooth has a whole-tooth condition, don't apply surface markings
          if (current.toothCondition) return prev;
          const newSurfaces = { ...current.surfaces };
          newSurfaces[surface] = newSurfaces[surface] === cond ? null : cond;
          const updated = { ...current, surfaces: newSurfaces };
          return updateTooth(prev, key, updated);
        });
      },
      [activeCondition],
    );

    const handleClickWhole = useCallback(
      (toothNo: number) => {
        if (activeCondition === null) return;
        if (PER_ZONE_SET.has(activeCondition)) return;
        setData((prev) =>
          applyWholeCondition(prev, toothNo, activeCondition as WholeToothCondition),
        );
      },
      [activeCondition],
    );

    const togglePrimary = useCallback(() => {
      setData((prev) => ({ ...prev, showPrimary: !prev.showPrimary }));
    }, []);

    const rowProps = {
      data,
      activeCondition,
      onClickSurface: handleClickSurface,
      onClickWhole: handleClickWhole,
    };

    return (
      <div className={css({ bg: "white" })}>
        <ConditionPalette active={activeCondition} onChange={setActiveCondition} />

        {/* Toggle dentición temporal */}
        <div
          className={css({
            display: "flex",
            alignItems: "center",
            gap: "2",
            px: "4",
            py: "2",
            borderBottomWidth: "1px",
            borderColor: "#e2e8f0",
          })}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "11px",
              color: "#64748b",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={data.showPrimary}
              onChange={togglePrimary}
              style={{ cursor: "pointer" }}
            />
            Mostrar dentición temporal (pediátrico)
          </label>
        </div>

        {/* Grid — en tablet/desktop (contenedor ≥ 672px) entra sin scroll; en mobile
            angosto scrollea horizontalmente con snap + degradado de overflow. */}
        <div className={cf.scrollFadeXOuter} style={{ ["--scroll-fade-bg" as string]: "#ffffff" }}>
          <div
            className={cx(cf.scrollFadeXInner, css({ scrollSnapType: { base: "x proximity", md: "none" } }))}
            style={{ padding: "16px" }}
          >
            <div style={{ minWidth: "640px", display: "inline-block", scrollSnapAlign: "start" }}>
              {/* Upper permanent */}
              <TeethRow
                teeth={[...Q1, ...Q2]}
                numberPosition="below"
                {...rowProps}
              />

              {/* Upper primary */}
              {data.showPrimary && (
                <div style={{ paddingLeft: "2px", marginTop: "4px" }}>
                  <div style={{ display: "flex", gap: "2px" }}>
                    {/* 154px centers primary row (302px) under permanent row (610px) */}
                    <div style={{ width: "154px", flexShrink: 0 }} />
                    <TeethRow
                      teeth={[...PRI_UR, ...PRI_UL]}
                      numberPosition="below"
                      isPrimary
                      {...rowProps}
                    />
                  </div>
                </div>
              )}

              {/* Midline gap */}
              <div style={{ height: "8px" }} />

              {/* Lower primary */}
              {data.showPrimary && (
                <div style={{ paddingLeft: "2px", marginBottom: "4px" }}>
                  <div style={{ display: "flex", gap: "2px" }}>
                    <div style={{ width: `${3 * 30}px`, flexShrink: 0 }} />
                    <TeethRow
                      teeth={[...PRI_LR, ...PRI_LL]}
                      numberPosition="above"
                      isPrimary
                      {...rowProps}
                    />
                  </div>
                </div>
              )}

              {/* Lower permanent */}
              <TeethRow
                teeth={[...Q4, ...Q3]}
                numberPosition="above"
                {...rowProps}
              />
            </div>
          </div>
        </div>
      </div>
    );
  },
);

// ── State helpers ─────────────────────────────────────────────────────────────

function updateTooth(
  prev: SimpleOdontogramData,
  key: string,
  updated: ToothState,
): SimpleOdontogramData {
  const newTeeth = { ...prev.teeth };
  if (toothHasAny(updated)) {
    newTeeth[key] = updated;
  } else {
    delete newTeeth[key];
  }
  return { ...prev, teeth: newTeeth };
}

function applyWholeCondition(
  prev: SimpleOdontogramData,
  toothNo: number,
  cond: WholeToothCondition,
): SimpleOdontogramData {
  const key = String(toothNo);
  const current = prev.teeth[key] ?? EMPTY_TOOTH;
  const updated: ToothState = {
    ...current,
    toothCondition: current.toothCondition === cond ? null : cond,
  };
  return updateTooth(prev, key, updated);
}
